import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import { cn } from '../../utils';

export interface PaginatedSelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface PaginatedSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  inputSize?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  multiple?: boolean;
  
  // Pagination props
  loadOptions: (page: number, search?: string) => Promise<{
    data: PaginatedSelectOption[];
    hasMore: boolean;
    total?: number;
  }>;
  searchable?: boolean;
  searchPlaceholder?: string;
  
  // Callbacks
  onChange?: (value: string | number | (string | number)[], option?: PaginatedSelectOption | PaginatedSelectOption[]) => void;
  onBlur?: () => void;
  onLoadMore?: (hasMore: boolean) => void;
}

export const PaginatedSelect = forwardRef<HTMLSelectElement, PaginatedSelectProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      placeholder = "Select an option...",
      inputSize = 'md',
      loading = false,
      id,
      required,
      disabled = false,
      multiple = false,
      'aria-describedby': _ariaDescribedBy,
      value,
      onChange,
      onBlur: _onBlur,
      
      // Pagination props
      loadOptions,
      searchable = false,
      searchPlaceholder = "Search...",
      
      // Callbacks
      onLoadMore,
      ...props
    },
    ref
  ) => {
    const selectRef = useRef<HTMLSelectElement>(null);
    const selectId = id || `paginated-select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const helperId = `${selectId}-helper`;

    const [options, setOptions] = useState<PaginatedSelectOption[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOption, setSelectedOption] = useState<PaginatedSelectOption | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<PaginatedSelectOption[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Combine external ref with internal ref
    useImperativeHandle(ref, () => selectRef.current as HTMLSelectElement);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
      
      return undefined;
    }, [isOpen]);

    const hasError = Boolean(error);

    // Load initial options
    useEffect(() => {
      const loadInitialOptions = async () => {
        try {
          setIsInitialLoading(true);
          const result = await loadOptions(1, searchTerm);
          setOptions(result.data);
          setHasMore(result.hasMore);
          setCurrentPage(1);
        } catch (error) {
          console.error('Error loading initial options:', error);
          setOptions([]);
          setHasMore(false);
        } finally {
          setIsInitialLoading(false);
        }
      };

      loadInitialOptions();
    }, [loadOptions, searchTerm]);

    // Update selected options when value changes
    useEffect(() => {
      if (multiple) {
        if (Array.isArray(value)) {
          const selected = options.filter(option => value.includes(option.value));
          setSelectedOptions(selected);
        } else {
          setSelectedOptions([]);
        }
      } else {
        if (value !== undefined && value !== '') {
          const selected = options.find(option => option.value === value);
          setSelectedOption(selected || null);
        } else {
          setSelectedOption(null);
        }
      }
    }, [value, options, multiple]);

    const handleLoadMore = async () => {
      if (isLoadingMore || !hasMore) return;

      try {
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        const result = await loadOptions(nextPage, searchTerm);
        
        setOptions(prev => [...prev, ...result.data]);
        setHasMore(result.hasMore);
        setCurrentPage(nextPage);
        
        onLoadMore?.(result.hasMore);
      } catch (error) {
        console.error('Error loading more options:', error);
      } finally {
        setIsLoadingMore(false);
      }
    };

    const handleSearch = (searchValue: string) => {
      setSearchTerm(searchValue);
      setCurrentPage(1);
    };

    const handleOptionSelect = (option: PaginatedSelectOption) => {
      if (multiple) {
        const newSelectedOptions = selectedOptions.find(opt => opt.value === option.value)
          ? selectedOptions.filter(opt => opt.value !== option.value)
          : [...selectedOptions, option];
        
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions.map(opt => opt.value), newSelectedOptions);
      } else {
        setSelectedOption(option);
        onChange?.(option.value, option);
        setIsOpen(false);
      }
    };

    const handleRemoveOption = (optionValue: string | number) => {
      if (multiple) {
        const newSelectedOptions = selectedOptions.filter(opt => opt.value !== optionValue);
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions.map(opt => opt.value), newSelectedOptions);
      }
    };

    const handleClearAll = () => {
      if (multiple) {
        setSelectedOptions([]);
        onChange?.([], []);
      } else {
        setSelectedOption(null);
        onChange?.('', undefined);
      }
    };

    const sizeClasses = {
      sm: 'text-sm px-3 py-2',
      md: 'text-sm px-3 py-2',
      lg: 'text-base px-4 py-3',
    };

    const baseClasses = cn(
      'block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm',
      sizeClasses[inputSize],
      hasError && 'border-red-300 focus:border-red-500 focus:ring-red-500',
      disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed',
      className
    );


    return (
      <div className="relative" ref={dropdownRef}>
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}


        {/* Selected Options Display */}
        {multiple && selectedOptions.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {selectedOptions.map((option) => (
              <div
                key={option.value}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200"
              >
                <span>{option.label}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(option.value)}
                  className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-800 focus:outline-none"
            >
              Clear All
            </button>
          </div>
        )}

        {/* Dropdown Button */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              baseClasses,
              'text-left flex items-center justify-between',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            )}
            disabled={loading || isInitialLoading}
          >
            <span className={cn(
              'block truncate',
              (!selectedOption && !multiple) && 'text-gray-500'
            )}>
              {isInitialLoading ? 'Loading...' : 
               multiple ? (selectedOptions.length > 0 ? `${selectedOptions.length} selected` : placeholder) :
               selectedOption ? selectedOption.label : placeholder}
            </span>
            <svg
              className={cn(
                'h-5 w-5 text-gray-400 transition-transform',
                isOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Options */}
          {isOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
              {/* Search Input (if searchable) */}
              {searchable && (
                <div className="px-3 py-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              {options.length === 0 && !isInitialLoading ? (
                <div className="px-3 py-2 text-gray-500 text-sm">No options found</div>
              ) : (
                options.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    disabled={option.disabled}
                    className={cn(
                      'block w-full px-3 py-2 cursor-pointer text-left text-sm hover:bg-gray-100',
                      option.disabled && 'opacity-50 cursor-not-allowed',
                      multiple && selectedOptions.find(opt => opt.value === option.value) && 'bg-blue-50 text-blue-900',
                      !multiple && selectedOption?.value === option.value && 'bg-blue-50 text-blue-900'
                    )}
                  >
                    {option.label}
                  </button>
                ))
              )}

              {/* Load More Button */}
              {hasMore && options.length > 0 && !isInitialLoading && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 focus:outline-none focus:bg-blue-50 disabled:opacity-50"
                  >
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p id={errorId} className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {helperText && (
          <p id={helperId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}

        {/* Hidden select for form submission */}
        <select
          key={`${multiple ? 'multiple' : 'single'}-${Array.isArray(value) ? value.join(',') : value || ''}`}
          ref={selectRef}
          id={selectId}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          defaultValue={multiple ? (Array.isArray(value) ? value : []) : value || ''}
          multiple={multiple}
          {...props}
        >
          {multiple ? (
            selectedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          ) : (
            selectedOption && (
              <option value={selectedOption.value}>
                {selectedOption.label}
              </option>
            )
          )}
        </select>
      </div>
    );
  }
);

PaginatedSelect.displayName = 'PaginatedSelect';